import { useState, useEffect, useCallback } from 'react';
import {
  BarChart, Bar, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, AreaChart, Area, ComposedChart,
  ReferenceLine, PieChart, Pie, Cell,
} from 'recharts';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { capitalStructureApi } from '../../api/capitalStructure';

// ── Theme Constants (match old-money palette) ──────────────
const COLORS = {
  navy: '#1B2A4A',
  navyLight: '#2A3D66',
  navyMuted: '#3A5080',
  burgundy: '#7A2E3B',
  burgundyMuted: 'rgba(122,46,59,0.12)',
  gold: '#B8953E',
  goldMuted: 'rgba(184,149,62,0.15)',
  hunter: '#2D5F4A',
  hunterMuted: 'rgba(45,95,74,0.12)',
  ivory: '#FAF7F2',
  cream: '#F3EDE4',
  parchment: '#EBE3D6',
  ink: '#1A1A1A',
  textBody: '#3D3830',
  textMuted: '#7A7265',
  textLight: '#A69D91',
  border: '#D6CCBE',
  borderLight: '#E8E0D4',
};

const PIE_COLORS = [COLORS.navy, COLORS.navyLight, COLORS.burgundy, COLORS.hunter, COLORS.gold];

// ── Helpers ────────────────────────────────────────────────
function fmt(v: number | null | undefined): string {
  if (v == null) return '—';
  const abs = Math.abs(v);
  const neg = v < 0;
  if (abs >= 1_000_000) return `${neg ? '(' : ''}$${(abs / 1_000_000).toFixed(1)}M${neg ? ')' : ''}`;
  if (abs >= 1000) return `${neg ? '(' : ''}$${(abs / 1000).toFixed(0)}K${neg ? ')' : ''}`;
  return `${neg ? '(' : ''}$${abs.toFixed(0)}${neg ? ')' : ''}`;
}

function pct(v: number): string {
  return `${(v * 100).toFixed(1)}%`;
}

// ── Shared Sub-Components ──────────────────────────────────

function StatCard({ label, value, sub, accent }: {
  label: string; value: string; sub?: string; accent?: string;
}) {
  return (
    <div className="flex-1 min-w-[140px] px-5 py-4 border-r border-ivory-300 last:border-r-0">
      <p className="text-[10px] font-sans font-bold text-stone-400 tracking-widest uppercase mb-2">
        {label}
      </p>
      <p
        className="text-2xl font-display font-semibold leading-none"
        style={{ color: accent || COLORS.ink }}
      >
        {value}
      </p>
      {sub && (
        <p className="text-[11px] font-sans text-stone-500 mt-1.5">{sub}</p>
      )}
    </div>
  );
}

function SectionTitle({ children, sub }: { children: React.ReactNode; sub?: string }) {
  return (
    <div className="mb-4">
      <h3 className="text-[13px] font-sans font-bold text-stone-500 tracking-widest uppercase border-b-2 border-navy-800 inline-block pb-1">
        {children}
      </h3>
      {sub && (
        <p className="text-[13px] font-body text-stone-400 italic mt-1.5">{sub}</p>
      )}
    </div>
  );
}

function Card({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`bg-ivory-50 border border-stone-300 rounded-sm ${className}`}>
      {children}
    </div>
  );
}

function AlertBox({ type, title, children }: {
  type: 'danger' | 'warning' | 'success' | 'info';
  title?: string;
  children: React.ReactNode;
}) {
  const styles = {
    danger: { bg: 'bg-red-50', border: 'border-l-red-800', titleColor: 'text-red-800' },
    warning: { bg: 'bg-amber-50', border: 'border-l-amber-600', titleColor: 'text-amber-700' },
    success: { bg: 'bg-emerald-50', border: 'border-l-emerald-700', titleColor: 'text-emerald-700' },
    info: { bg: 'bg-blue-50', border: 'border-l-blue-800', titleColor: 'text-blue-800' },
  };
  const s = styles[type];

  return (
    <div className={`${s.bg} ${s.border} border-l-[3px] rounded-sm p-3.5 mb-2.5`}>
      {title && (
        <p className={`font-sans text-[13px] font-bold ${s.titleColor} mb-1`}>{title}</p>
      )}
      <div className="font-body text-[12px] text-stone-700 leading-relaxed">{children}</div>
    </div>
  );
}

function StatusPill({ status, label }: { status: string; label: string }) {
  const map: Record<string, string> = {
    compliant: 'bg-emerald-100 text-emerald-800',
    current: 'bg-emerald-100 text-emerald-800',
    watch: 'bg-amber-100 text-amber-800',
    breach: 'bg-red-100 text-red-800',
    danger: 'bg-red-100 text-red-800',
    info: 'bg-blue-100 text-blue-800',
  };
  return (
    <span className={`inline-block px-2.5 py-0.5 rounded-sm text-[10px] font-sans font-bold tracking-wide uppercase ${map[status] || map.info}`}>
      {label}
    </span>
  );
}

function TrendIcon({ trend }: { trend: string }) {
  if (trend === 'improving') return <TrendingUp size={14} className="text-emerald-600" />;
  if (trend === 'deteriorating') return <TrendingDown size={14} className="text-red-600" />;
  return <Minus size={14} className="text-amber-500" />;
}

const ChartTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload) return null;
  return (
    <div className="bg-ivory-50 border border-stone-300 rounded-sm px-3.5 py-2.5 shadow-lg">
      <p className="text-[11px] font-sans font-semibold text-stone-400 tracking-wide mb-1">{label}</p>
      {payload.map((e: any, i: number) => (
        <p key={i} className="text-[12px] font-sans font-medium" style={{ color: e.color || COLORS.ink }}>
          {e.name}: {typeof e.value === 'number' && e.value < 10 ? `${e.value.toFixed(2)}×` : fmt(e.value)}
        </p>
      ))}
    </div>
  );
};

// ── Panel: Leverage Overview ───────────────────────────────

function LeverageOverviewPanel({ trajectory, debtSchedule, metrics }: any) {
  return (
    <div>
      <div className="grid grid-cols-2 gap-5 mb-5">
        <Card className="p-6">
          <SectionTitle sub="Total debt and LTV trajectory over the planning horizon">
            Leverage Trajectory
          </SectionTitle>
          <ResponsiveContainer width="100%" height={260}>
            <ComposedChart data={trajectory}>
              <defs>
                <linearGradient id="debtGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={COLORS.navy} stopOpacity={0.2} />
                  <stop offset="95%" stopColor={COLORS.navy} stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={COLORS.borderLight} />
              <XAxis dataKey="year" tick={{ fill: COLORS.textLight, fontSize: 12 }} axisLine={{ stroke: COLORS.border }} />
              <YAxis yAxisId="debt" tick={{ fill: COLORS.textLight, fontSize: 11 }} axisLine={{ stroke: COLORS.border }} tickFormatter={(v: number) => fmt(v)} />
              <YAxis yAxisId="ltv" orientation="right" tick={{ fill: COLORS.textLight, fontSize: 11 }} axisLine={{ stroke: COLORS.border }} tickFormatter={(v: number) => `${v}%`} domain={[0, 65]} />
              <Tooltip content={<ChartTooltip />} />
              <Area yAxisId="debt" type="monotone" dataKey="totalDebt" stroke={COLORS.navy} strokeWidth={2} fill="url(#debtGrad)" name="Total Debt" />
              <Line yAxisId="ltv" type="monotone" dataKey="ltv" stroke={COLORS.burgundy} strokeWidth={2.5} dot={{ fill: COLORS.burgundy, r: 4, strokeWidth: 0 }} name="LTV %" />
              <ReferenceLine yAxisId="ltv" y={55} stroke={COLORS.burgundy} strokeDasharray="6 3" />
            </ComposedChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-6">
          <SectionTitle sub="Debt service coverage ratio with covenant floor">
            DSCR Trajectory
          </SectionTitle>
          <ResponsiveContainer width="100%" height={260}>
            <ComposedChart data={trajectory}>
              <CartesianGrid strokeDasharray="3 3" stroke={COLORS.borderLight} />
              <XAxis dataKey="year" tick={{ fill: COLORS.textLight, fontSize: 12 }} axisLine={{ stroke: COLORS.border }} />
              <YAxis tick={{ fill: COLORS.textLight, fontSize: 11 }} axisLine={{ stroke: COLORS.border }} domain={[0.8, 2.0]} tickFormatter={(v: number) => `${v.toFixed(1)}×`} />
              <Tooltip content={<ChartTooltip />} />
              <Bar dataKey="noi" fill={COLORS.hunter} radius={[3, 3, 0, 0]} name="NOI" barSize={28} fillOpacity={0.3} />
              <Bar dataKey="debtService" fill={COLORS.burgundy} radius={[3, 3, 0, 0]} name="Debt Service" barSize={28} fillOpacity={0.3} />
              <Line type="monotone" dataKey="dscr" stroke={COLORS.gold} strokeWidth={2.5} dot={{ fill: COLORS.gold, r: 5, strokeWidth: 0 }} name="DSCR" />
              <ReferenceLine y={1.10} stroke={COLORS.burgundy} strokeDasharray="6 3" />
            </ComposedChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <Card className="overflow-hidden">
        <div className="px-6 pt-5 pb-3">
          <SectionTitle>Debt Schedule Detail</SectionTitle>
        </div>
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-ivory-200">
              {['Instrument', 'Type', 'Balance', 'Rate', 'Structure', 'Maturity', 'Ann. Service', 'Status'].map((h) => (
                <th key={h} className="text-[10px] font-sans font-bold text-stone-400 tracking-widest uppercase text-left px-3.5 py-2.5 border-b-2 border-stone-300">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {debtSchedule.map((d: any, i: number) => (
              <tr key={d.id || i} className={i % 2 === 0 ? '' : 'bg-ivory-200'}>
                <td className="px-3.5 py-3 text-[13px] font-sans font-semibold text-stone-800 border-b border-stone-200">{d.name}</td>
                <td className="px-3.5 py-3 text-[13px] font-sans text-stone-600 border-b border-stone-200">{d.lien_priority || d.liability_type}</td>
                <td className="px-3.5 py-3 text-[13px] font-mono text-stone-800 border-b border-stone-200">{fmt(parseFloat(d.outstanding_balance))}</td>
                <td className="px-3.5 py-3 text-[13px] font-mono text-stone-600 border-b border-stone-200">
                  {d.is_variable_rate ? `SOFR+${Math.round(parseFloat(d.interest_rate) * 10000 - 500)}` : `${(parseFloat(d.interest_rate) * 100).toFixed(2)}%`}
                </td>
                <td className="px-3.5 py-3 text-[13px] font-sans text-stone-600 border-b border-stone-200">{d.amortization_type || 'Amortizing'}</td>
                <td className="px-3.5 py-3 text-[13px] font-sans text-stone-600 border-b border-stone-200">
                  {d.maturity_date ? new Date(d.maturity_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : '—'}
                </td>
                <td className="px-3.5 py-3 text-[13px] font-mono text-stone-600 border-b border-stone-200">
                  {d.annual_debt_service ? fmt(parseFloat(d.annual_debt_service)) : '—'}
                </td>
                <td className="px-3.5 py-3 border-b border-stone-200">
                  <StatusPill
                    status={parseFloat(d.dscr || '2') < 1.1 ? 'watch' : 'current'}
                    label={parseFloat(d.dscr || '2') < 1.1 ? 'Watch' : 'Current'}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="flex justify-between items-center px-6 py-3.5 bg-ivory-200 border-t border-stone-200">
          <span className="text-[12px] font-sans font-bold text-stone-700">Total Outstanding</span>
          <span className="text-[14px] font-mono font-bold" style={{ color: COLORS.navy }}>
            {fmt(metrics?.totalDebt || 0)}
          </span>
        </div>
      </Card>
    </div>
  );
}


// ── Covenant Form Helpers ──────────────────────────────────
const COVENANT_TYPE_OPTIONS = [
  { value: 'max_ltv', label: 'Max LTV' },
  { value: 'min_dscr', label: 'Min DSCR' },
  { value: 'min_liquidity', label: 'Min Liquidity' },
  { value: 'max_leverage', label: 'Max Leverage' },
  { value: 'min_net_worth', label: 'Min Net Worth' },
  { value: 'max_debt_yield', label: 'Max Debt Yield' },
  { value: 'other', label: 'Other' },
];

const FREQ_OPTIONS = ['quarterly', 'monthly', 'semi-annual', 'annual'];

function thresholdHint(type: string) {
  switch (type) {
    case 'max_ltv': case 'max_leverage': return 'Decimal — e.g. 0.65 for 65%';
    case 'min_dscr': case 'max_debt_yield': return 'Ratio — e.g. 1.10';
    case 'min_liquidity': case 'min_net_worth': return 'Dollar amount — e.g. 500000';
    default: return 'Numeric value';
  }
}

function autoDisplay(type: string, val: number): string {
  if (isNaN(val)) return '';
  switch (type) {
    case 'max_ltv': case 'max_leverage': return `${(val * 100).toFixed(0)}%`;
    case 'min_dscr': case 'max_debt_yield': return `${val.toFixed(2)}\u00d7`;
    case 'min_liquidity': case 'min_net_worth':
      return val >= 1000000 ? `$${(val / 1000000).toFixed(1)}M` : `$${(val / 1000).toFixed(0)}K`;
    default: return String(val);
  }
}

// ── Covenant Form Modal ────────────────────────────────────

function CovenantFormModal({ item, liabilities, onSave, onClose }: {
  item: any | null;
  liabilities: any[];
  onSave: (data: any) => void;
  onClose: () => void;
}) {
  const [form, setForm] = useState({
    liabilityId: item?.liability_id || '',
    name: item?.name || '',
    covenantType: item?.covenant_type || 'max_ltv',
    thresholdValue: item ? String(item.threshold_value) : '',
    watchThresholdPct: item?.watch_threshold_pct ?? 10,
    testFrequency: item?.test_frequency || 'quarterly',
    notes: item?.notes || '',
  });

  const set = (k: string, v: any) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = () => {
    const tv = parseFloat(form.thresholdValue);
    if (!form.liabilityId || !form.name || isNaN(tv)) return;
    onSave({
      liabilityId: form.liabilityId,
      name: form.name,
      covenantType: form.covenantType,
      thresholdValue: tv,
      thresholdDisplay: autoDisplay(form.covenantType, tv),
      watchThresholdPct: form.watchThresholdPct,
      testFrequency: form.testFrequency,
      notes: form.notes,
    });
  };

  const inputCls = 'w-full px-3 py-2 text-[13px] font-sans border rounded bg-white focus:outline-none focus:ring-1 focus:ring-[#1B2A4A] border-stone-300';
  const labelCls = 'block text-[11px] font-sans font-bold text-stone-500 tracking-wider uppercase mb-1';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.4)' }}>
      <div className="bg-white rounded shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-stone-200 flex justify-between items-center">
          <h3 className="text-lg font-display font-bold text-stone-800">{item ? 'Edit Covenant' : 'Add Covenant'}</h3>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-600 text-xl leading-none">&times;</button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div>
            <label className={labelCls}>Credit Facility *</label>
            <select value={form.liabilityId} onChange={e => set('liabilityId', e.target.value)} className={inputCls}>
              <option value="">Select a liability...</option>
              {liabilities.map((l: any) => (
                <option key={l.id} value={l.id}>{l.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Covenant Name *</label>
            <input value={form.name} onChange={e => set('name', e.target.value)} className={inputCls} placeholder="e.g. Max LTV — First Lien" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Covenant Type *</label>
              <select value={form.covenantType} onChange={e => set('covenantType', e.target.value)} className={inputCls}>
                {COVENANT_TYPE_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Threshold Value *</label>
              <input type="number" step="any" value={form.thresholdValue} onChange={e => set('thresholdValue', e.target.value)} className={inputCls} placeholder={thresholdHint(form.covenantType)} />
              {form.thresholdValue && (
                <p className="text-[11px] font-mono text-stone-400 mt-1">Display: {autoDisplay(form.covenantType, parseFloat(form.thresholdValue))}</p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Watch Threshold %</label>
              <input type="number" value={form.watchThresholdPct} onChange={e => set('watchThresholdPct', parseFloat(e.target.value) || 10)} className={inputCls} />
              <p className="text-[11px] text-stone-400 mt-1">Alert when headroom is within this % of threshold</p>
            </div>
            <div>
              <label className={labelCls}>Test Frequency</label>
              <select value={form.testFrequency} onChange={e => set('testFrequency', e.target.value)} className={inputCls}>
                {FREQ_OPTIONS.map(f => (
                  <option key={f} value={f}>{f.charAt(0).toUpperCase() + f.slice(1)}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className={labelCls}>Notes</label>
            <textarea value={form.notes} onChange={e => set('notes', e.target.value)} className={inputCls} rows={2} placeholder="Optional notes about this covenant..." />
          </div>
        </div>
        <div className="px-6 py-4 border-t border-stone-200 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-[13px] font-sans font-semibold text-stone-600 border border-stone-300 rounded hover:bg-stone-50">Cancel</button>
          <button onClick={handleSubmit} className="px-4 py-2 text-[13px] font-sans font-semibold text-white rounded" style={{ background: '#1B2A4A' }}>
            {item ? 'Save Changes' : 'Add Covenant'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Panel: Covenant Monitor ────────────────────────────────

function CovenantMonitorPanel({ covenants, trajectory, onAdd, onEdit, onDelete }: any) {
  return (
    <div>
      <Card className="overflow-hidden mb-5">
        <div className="px-6 pt-5 pb-3">
          <div className="flex justify-between items-start">
            <SectionTitle sub="Real-time covenant compliance across all credit facilities">
              Covenant Compliance Dashboard
            </SectionTitle>
            <button onClick={onAdd} className="px-3 py-1.5 text-[11px] font-sans font-bold tracking-wider uppercase rounded" style={{ background: '#1B2A4A', color: 'white' }}>
              + Add Covenant
            </button>
          </div>
        </div>
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-ivory-200">
              {['Covenant', 'Threshold', 'Current', 'Headroom', 'Trend', 'Status', ''].map((h) => (
                <th key={h} className="text-[10px] font-sans font-bold text-stone-400 tracking-widest uppercase text-left px-3.5 py-2.5 border-b-2 border-stone-300">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {covenants.map((c: any, i: number) => (
              <tr key={c.id || i} className={i % 2 === 0 ? '' : 'bg-ivory-200'}>
                <td className="px-3.5 py-3 text-[13px] font-sans font-semibold text-stone-800 border-b border-stone-200">{c.name}</td>
                <td className="px-3.5 py-3 text-[13px] font-mono text-stone-600 border-b border-stone-200">{c.threshold_display}</td>
                <td className="px-3.5 py-3 text-[13px] font-mono text-stone-600 border-b border-stone-200">{c.current_display || '—'}</td>
                <td className="px-3.5 py-3 text-[13px] font-mono border-b border-stone-200" style={{ color: c.status === 'watch' ? COLORS.gold : COLORS.hunter }}>
                  {c.headroom_display || '—'}
                </td>
                <td className="px-3.5 py-3 border-b border-stone-200">
                  <div className="flex items-center gap-1.5">
                    <TrendIcon trend={c.trend || 'stable'} />
                    <span className="text-[11px] font-sans font-semibold capitalize" style={{ color: c.trend === 'improving' ? COLORS.hunter : COLORS.gold }}>
                      {c.trend || 'Stable'}
                    </span>
                  </div>
                </td>
                <td className="px-3.5 py-3 border-b border-stone-200">
                  <StatusPill
                    status={c.status}
                    label={c.status === 'compliant' ? 'Compliant' : c.status === 'watch' ? 'Tight' : 'Breach'}
                  />
                </td>
                <td className="px-3.5 py-3 border-b border-stone-200">
                  <div className="flex gap-2">
                    <button onClick={() => onEdit(c)} className="text-[11px] font-sans font-semibold hover:underline" style={{ color: '#1B2A4A' }}>Edit</button>
                    <button onClick={() => onDelete(c.id)} className="text-[11px] font-sans font-semibold hover:underline" style={{ color: '#7A2E3B' }}>Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <div className="grid grid-cols-2 gap-5">
        <Card className="p-6">
          <SectionTitle>Covenant Alerts</SectionTitle>
          {covenants.filter((c: any) => c.status === 'watch').length > 0 ? (
            covenants.filter((c: any) => c.status === 'watch').map((c: any, i: number) => (
              <AlertBox key={i} type="warning" title={`${c.name} — Near Threshold`}>
                Current value of {c.current_display} against {c.threshold_display} threshold.
                Headroom of {c.headroom_display} is within watch range. Monitor closely.
              </AlertBox>
            ))
          ) : (
            <AlertBox type="success" title="All Covenants Compliant">
              All covenant tests are passing with adequate headroom.
            </AlertBox>
          )}
          {covenants.filter((c: any) => c.status === 'breach').map((c: any, i: number) => (
            <AlertBox key={i} type="danger" title={`${c.name} — BREACH`}>
              Current value of {c.current_display} has breached the {c.threshold_display} threshold.
              Immediate lender communication required.
            </AlertBox>
          ))}
          {covenants.filter((c: any) => c.trend === 'improving').length > 0 && (
            <AlertBox type="success" title="Positive Trend">
              {covenants.filter((c: any) => c.trend === 'improving').length} covenant(s) showing improving trajectory from deleveraging.
            </AlertBox>
          )}
        </Card>

        <Card className="p-6">
          <SectionTitle sub="DSCR headroom above covenant floor by year">
            Covenant Headroom Projection
          </SectionTitle>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={trajectory.map((d: any) => ({ year: d.year, headroom: +(d.dscr - 1.10).toFixed(2) }))}>
              <CartesianGrid strokeDasharray="3 3" stroke={COLORS.borderLight} />
              <XAxis dataKey="year" tick={{ fill: COLORS.textLight, fontSize: 12 }} axisLine={{ stroke: COLORS.border }} />
              <YAxis tick={{ fill: COLORS.textLight, fontSize: 11 }} axisLine={{ stroke: COLORS.border }} tickFormatter={(v: number) => `${v.toFixed(2)}×`} />
              <Tooltip content={<ChartTooltip />} />
              <ReferenceLine y={0} stroke={COLORS.burgundy} strokeWidth={2} />
              <Bar dataKey="headroom" name="DSCR Headroom" barSize={32} radius={[3, 3, 0, 0]}>
                {trajectory.map((d: any, i: number) => (
                  <Cell key={i} fill={(d.dscr - 1.10) < 0.05 ? COLORS.gold : COLORS.hunter} fillOpacity={0.8} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>
    </div>
  );
}

// ── Panel: Maturity & Rate ─────────────────────────────────

function MaturityRatePanel({ maturityProfile, rateExposure, trajectory, metrics }: any) {
  const fixedPct = metrics?.fixedRatePct || 0;
  const floatingPct = metrics?.floatingRatePct || 0;

  return (
    <div>
      <div className="grid grid-cols-2 gap-5 mb-5">
        <Card className="p-6">
          <SectionTitle sub="Balloon maturities and scheduled amortization by year">
            Debt Maturity Profile
          </SectionTitle>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={maturityProfile} barGap={2}>
              <CartesianGrid strokeDasharray="3 3" stroke={COLORS.borderLight} />
              <XAxis dataKey="year" tick={{ fill: COLORS.textLight, fontSize: 12 }} axisLine={{ stroke: COLORS.border }} />
              <YAxis tick={{ fill: COLORS.textLight, fontSize: 11 }} axisLine={{ stroke: COLORS.border }} tickFormatter={(v: number) => fmt(v)} />
              <Tooltip content={<ChartTooltip />} />
              <Bar dataKey="maturingBalance" fill={COLORS.burgundy} name="Balloon Maturity" barSize={32} fillOpacity={0.8} radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
          {maturityProfile.filter((m: any) => m.maturingBalance > 5000000).map((m: any, i: number) => (
            <AlertBox key={i} type="warning" title={`${m.year} Maturity Wall`}>
              {fmt(m.maturingBalance)} in balloon maturities due in {m.year}.
              {m.instrumentCount > 1 ? ` Across ${m.instrumentCount} instruments.` : ''}
              {' '}Begin lender discussions 12–18 months prior.
            </AlertBox>
          ))}
        </Card>

        <Card className="p-6">
          <SectionTitle sub="Fixed versus floating rate exposure across the debt stack">
            Interest Rate Exposure
          </SectionTitle>
          <div className="flex gap-5 mt-2.5">
            <ResponsiveContainer width="45%" height={200}>
              <PieChart>
                <Pie
                  data={rateExposure}
                  cx="50%" cy="50%"
                  innerRadius={50} outerRadius={80}
                  dataKey="balance"
                  stroke={COLORS.ivory}
                  strokeWidth={2}
                >
                  {rateExposure.map((_: any, i: number) => (
                    <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip content={<ChartTooltip />} />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex-1 flex flex-col justify-center gap-2">
              {rateExposure.map((r: any, i: number) => (
                <div key={i} className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                  <span className="flex-1 text-[12px] font-sans text-stone-600">{r.name}</span>
                  <span className="text-[12px] font-mono text-stone-800 font-semibold">{r.rate}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="flex gap-3 mt-4">
            {[
              { label: 'Fixed', value: pct(fixedPct), color: COLORS.navy },
              { label: 'Floating', value: pct(floatingPct), color: COLORS.burgundy },
            ].map((x, i) => (
              <div key={i} className="flex-1 px-4 py-3 bg-ivory-200 rounded-sm border-l-[3px]" style={{ borderLeftColor: x.color }}>
                <p className="text-[10px] font-sans text-stone-400 tracking-widest uppercase mb-1">{x.label}</p>
                <p className="text-lg font-mono font-semibold" style={{ color: x.color }}>{x.value}</p>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Card className="p-6">
        <SectionTitle sub="Annual interest expense trajectory as debt amortizes">
          Interest Expense Projection
        </SectionTitle>
        <ResponsiveContainer width="100%" height={200}>
          <AreaChart data={trajectory}>
            <defs>
              <linearGradient id="intGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={COLORS.burgundy} stopOpacity={0.15} />
                <stop offset="95%" stopColor={COLORS.burgundy} stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={COLORS.borderLight} />
            <XAxis dataKey="year" tick={{ fill: COLORS.textLight, fontSize: 12 }} axisLine={{ stroke: COLORS.border }} />
            <YAxis tick={{ fill: COLORS.textLight, fontSize: 11 }} axisLine={{ stroke: COLORS.border }} tickFormatter={(v: number) => fmt(v)} />
            <Tooltip content={<ChartTooltip />} />
            <Area type="monotone" dataKey="interestExp" stroke={COLORS.burgundy} strokeWidth={2} fill="url(#intGrad)" name="Interest Expense" />
          </AreaChart>
        </ResponsiveContainer>
      </Card>
    </div>
  );
}

// ── Panel: Contribution Scenarios ──────────────────────────

function ContributionScenariosPanel({ entities, members }: any) {
  const [selectedEntity, setSelectedEntity] = useState('');
  const [selectedMember, setSelectedMember] = useState('');
  const [amount, setAmount] = useState(10000000);
  const [results, setResults] = useState<Record<string, any>>({});
  const [isModeling, setIsModeling] = useState(false);

  const runAllScenarios = useCallback(async () => {
    if (!selectedEntity || !selectedMember) return;
    setIsModeling(true);
    try {
      const structures = ['dilution', 'preferred_equity', 'member_loan'] as const;
      const newResults: Record<string, any> = {};
      for (const s of structures) {
        const res = await capitalStructureApi.modelScenario({
          entityId: selectedEntity,
          contributorMemberId: selectedMember,
          amount,
          structure: s,
        });
        newResults[s] = res.data.data;
      }
      setResults(newResults);
    } catch (err) {
      console.error('Scenario modeling failed:', err);
    } finally {
      setIsModeling(false);
    }
  }, [selectedEntity, selectedMember, amount]);

  const hasResults = Object.keys(results).length > 0;

  return (
    <div>
      {/* Input Controls */}
      <Card className="p-6 mb-5">
        <SectionTitle>Model a Contribution</SectionTitle>
        <div className="grid grid-cols-4 gap-4 mb-4">
          <div>
            <label className="block text-[11px] font-sans font-bold text-stone-400 tracking-wide uppercase mb-1.5">Entity</label>
            <select
              value={selectedEntity}
              onChange={(e) => setSelectedEntity(e.target.value)}
              className="w-full px-3 py-2 bg-ivory-200 border border-stone-300 rounded-sm text-[13px] font-sans text-stone-800 focus:outline-none focus:border-navy-600"
            >
              <option value="">Select entity...</option>
              {entities.map((e: any) => (
                <option key={e.id} value={e.id}>{e.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[11px] font-sans font-bold text-stone-400 tracking-wide uppercase mb-1.5">Contributor</label>
            <select
              value={selectedMember}
              onChange={(e) => setSelectedMember(e.target.value)}
              className="w-full px-3 py-2 bg-ivory-200 border border-stone-300 rounded-sm text-[13px] font-sans text-stone-800 focus:outline-none focus:border-navy-600"
            >
              <option value="">Select member...</option>
              {members.map((m: any) => (
                <option key={m.id} value={m.id}>{m.first_name} {m.last_name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[11px] font-sans font-bold text-stone-400 tracking-wide uppercase mb-1.5">Amount</label>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(Number(e.target.value))}
              className="w-full px-3 py-2 bg-ivory-200 border border-stone-300 rounded-sm text-[13px] font-mono text-stone-800 focus:outline-none focus:border-navy-600"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={runAllScenarios}
              disabled={!selectedEntity || !selectedMember || isModeling}
              className="w-full px-4 py-2 rounded-sm text-[12px] font-sans font-bold text-ivory-100 disabled:opacity-50 transition-colors"
              style={{ background: COLORS.navy }}
            >
              {isModeling ? 'Modeling...' : 'Run All Structures'}
            </button>
          </div>
        </div>
      </Card>

      {hasResults && (
        <>
          {/* Summary Stats */}
          <Card className="flex mb-5 overflow-hidden flex-wrap">
            <StatCard label="Proposed Contribution" value={fmt(amount)} accent={COLORS.gold} />
            <StatCard label="Pre-Contribution Equity" value={fmt(results.dilution?.preEntityEquity)} sub="FMV less debt" />
            <StatCard label="Post-Equity (Dilution)" value={fmt(results.dilution?.postEntityEquity)} accent={COLORS.hunter} sub="Under dilution model" />
            <StatCard label="Structures Modeled" value="3" sub="Dilution · Preferred · Loan" accent={COLORS.navy} />
          </Card>

          {/* Comparison Matrix */}
          <Card className="overflow-hidden mb-5">
            <div className="px-6 pt-5 pb-3">
              <SectionTitle sub={`${fmt(amount)} contribution — three structuring alternatives`}>
                Structure Comparison Matrix
              </SectionTitle>
            </div>
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-ivory-200">
                  {['Metric', 'Straight Dilution', 'Preferred Capital', 'Member Loan'].map((h) => (
                    <th key={h} className="text-[10px] font-sans font-bold text-stone-400 tracking-widest uppercase text-center first:text-left px-3.5 py-2.5 border-b-2 border-stone-300">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  { metric: 'Post-Contribution Equity', d: fmt(results.dilution?.postEntityEquity), p: fmt(results.preferred_equity?.postEntityEquity), l: fmt(results.member_loan?.postEntityEquity) },
                  { metric: 'Contributor Ownership', d: `${results.dilution?.postContributorPct}%`, p: `${results.preferred_equity?.postContributorPct}%`, l: `${results.member_loan?.postContributorPct}%` },
                  { metric: 'Gift Tax Risk', d: results.dilution?.giftTaxRisk, p: results.preferred_equity?.giftTaxRisk, l: results.member_loan?.giftTaxRisk },
                  { metric: 'Leverage Impact', d: results.dilution?.leverageImpact, p: results.preferred_equity?.leverageImpact, l: results.member_loan?.leverageImpact },
                  { metric: '§2701 Concern', d: results.dilution?.section2701Concern ? 'Yes — review' : 'No', p: results.preferred_equity?.section2701Concern ? 'Yes — review' : 'No', l: results.member_loan?.section2701Concern ? 'Yes — review' : 'No' },
                ].map((row, i) => (
                  <tr key={i} className={i % 2 === 0 ? '' : 'bg-ivory-200'}>
                    <td className="px-3.5 py-3 text-[13px] font-sans font-semibold text-stone-800 border-b border-stone-200">{row.metric}</td>
                    <td className="px-3.5 py-3 text-[13px] font-sans text-stone-600 text-center border-b border-stone-200">{row.d}</td>
                    <td className="px-3.5 py-3 text-[13px] font-sans text-stone-600 text-center border-b border-stone-200">{row.p}</td>
                    <td className="px-3.5 py-3 text-[13px] font-sans text-stone-600 text-center border-b border-stone-200">{row.l}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>

          {/* Ownership Chart + Recommendations */}
          <div className="grid grid-cols-2 gap-5">
            <Card className="p-6">
              <SectionTitle>Ownership — Pre vs Post (Dilution)</SectionTitle>
              {results.dilution?.ownershipShifts && (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart
                    data={results.dilution.ownershipShifts.map((o: any) => ({
                      partner: o.memberName,
                      pre: o.prePct,
                      post: o.postPct,
                    }))}
                    layout="vertical"
                    barGap={4}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke={COLORS.borderLight} horizontal={false} />
                    <XAxis type="number" tick={{ fill: COLORS.textLight, fontSize: 11 }} axisLine={{ stroke: COLORS.border }} tickFormatter={(v: number) => `${v}%`} domain={[0, 55]} />
                    <YAxis type="category" dataKey="partner" tick={{ fill: COLORS.textMuted, fontSize: 12 }} axisLine={{ stroke: COLORS.border }} width={90} />
                    <Tooltip content={<ChartTooltip />} />
                    <Bar dataKey="pre" fill={COLORS.navy} name="Pre-Contribution" barSize={14} fillOpacity={0.6} radius={[0, 3, 3, 0]} />
                    <Bar dataKey="post" fill={COLORS.burgundy} name="Post-Contribution" barSize={14} fillOpacity={0.8} radius={[0, 3, 3, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Card>

            <Card className="p-6">
              <SectionTitle>Structure Recommendations</SectionTitle>
              {[
                { key: 'preferred_equity', label: 'Preferred Capital', rec: 'Recommended', status: 'current', color: COLORS.hunter },
                { key: 'member_loan', label: 'Member Loan', rec: 'Alternative', status: 'watch', color: COLORS.gold },
                { key: 'dilution', label: 'Straight Dilution', rec: 'Caution', status: 'danger', color: COLORS.burgundy },
              ].map((s) => (
                <div key={s.key} className="p-3.5 bg-ivory-200 rounded-sm border-l-[3px] mb-2" style={{ borderLeftColor: s.color }}>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-[13px] font-sans font-bold text-stone-800">{s.label}</span>
                    <StatusPill status={s.status} label={s.rec} />
                  </div>
                  <p className="text-[12px] font-body text-stone-600 leading-relaxed">
                    {results[s.key]?.notes}
                  </p>
                </div>
              ))}
              {results.preferred_equity?.section2701Concern && (
                <AlertBox type="info" title="§2701 — Preferred Capital">
                  A preferred equity structure may trigger §2701 valuation rules if the preferred return
                  is not "qualified." Ensure the preferred interest includes a cumulative, non-participating
                  right at a market rate. Review with estate counsel before execution.
                </AlertBox>
              )}
            </Card>
          </div>
        </>
      )}

      {!hasResults && (
        <Card className="p-12 text-center">
          <div className="text-stone-300 text-4xl mb-3">⚖</div>
          <p className="text-[14px] font-sans text-stone-500 mb-1">Select an entity, contributor, and amount above</p>
          <p className="text-[12px] font-sans text-stone-400">
            The scenario engine will model three structuring alternatives with gift tax,
            ownership, and §2701 analysis.
          </p>
        </Card>
      )}
    </div>
  );
}

// ── Main Page Component ────────────────────────────────────

export default function CapitalStructurePage() {
  const [panel, setPanel] = useState<'overview' | 'covenants' | 'maturity' | 'scenarios'>('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Data state
  const [metrics, setMetrics] = useState<any>(null);
  const [trajectory, setTrajectory] = useState<any[]>([]);
  const [debtSchedule, setDebtSchedule] = useState<any[]>([]);
  const [covenants, setCovenants] = useState<any[]>([]);
  const [maturityProfile, setMaturityProfile] = useState<any[]>([]);
  const [rateExposure, setRateExposure] = useState<any[]>([]);
  const [entities, setEntities] = useState<any[]>([]);
  const [members, setMembers] = useState<any[]>([]);
  const [covModal, setCovModal] = useState<{ open: boolean; item: any | null }>({ open: false, item: null });

  const reloadCovenants = async () => {
    try {
      const [covRes, trajRes, summaryRes] = await Promise.all([
        capitalStructureApi.covenants(),
        capitalStructureApi.trajectory(),
        capitalStructureApi.summary(),
      ]);
      setCovenants(covRes.data.data);
      setTrajectory(trajRes.data.data);
      setMetrics(summaryRes.data.data);
    } catch {}
  };

  const handleAddCovenant = () => setCovModal({ open: true, item: null });
  const handleEditCovenant = (c: any) => setCovModal({ open: true, item: c });
  const handleDeleteCovenant = async (id: string) => {
    if (!confirm('Delete this covenant?')) return;
    try {
      await capitalStructureApi.deleteCovenant(id);
      await reloadCovenants();
    } catch (err: any) {
      alert('Failed to delete covenant');
    }
  };
  const handleSaveCovenant = async (data: any) => {
    try {
      if (covModal.item) {
        await capitalStructureApi.updateCovenant(covModal.item.id, data);
      } else {
        await capitalStructureApi.createCovenant(data);
      }
      setCovModal({ open: false, item: null });
      await reloadCovenants();
    } catch (err: any) {
      alert('Failed to save covenant');
    }
  };

  useEffect(() => {
    async function loadData() {
      setLoading(true);
      setError(null);
      try {
        const [summaryRes, trajectoryRes, debtRes, covenantRes, maturityRes, rateRes] = await Promise.all([
          capitalStructureApi.summary(),
          capitalStructureApi.trajectory(),
          capitalStructureApi.debtSchedule(),
          capitalStructureApi.covenants().catch(() => ({ data: { data: [] } })),
          capitalStructureApi.maturityProfile(),
          capitalStructureApi.rateExposure(),
        ]);

        setMetrics(summaryRes.data.data);
        setTrajectory(trajectoryRes.data.data);
        setDebtSchedule(debtRes.data.data);
        setCovenants(covenantRes.data.data);
        setMaturityProfile(maturityRes.data.data);
        setRateExposure(rateRes.data.data);

        // Load entities and members for scenario panel (lazy)
        try {
          const [entRes, memRes] = await Promise.all([
            (await import('../../api/client')).default.get('/entities'),
            (await import('../../api/client')).default.get('/members'),
          ]);
          setEntities(entRes.data.data?.rows || entRes.data.data || []);
          setMembers(memRes.data.data?.rows || memRes.data.data || []);
        } catch {
          // Non-critical — scenarios just won't have options
        }
      } catch (err: any) {
        setError(err.message || 'Failed to load capital structure data');
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-[14px] font-sans text-stone-400 animate-pulse">Loading capital structure...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <AlertBox type="danger" title="Error Loading Data">{error}</AlertBox>
      </div>
    );
  }

  const panels = [
    { id: 'overview' as const, label: 'Leverage Overview' },
    { id: 'covenants' as const, label: 'Covenant Monitor' },
    { id: 'maturity' as const, label: 'Maturity & Rate' },
    { id: 'scenarios' as const, label: 'Contribution Scenarios' },
  ];

  return (
    <div>
      {/* KPI Bar */}
      <Card className="flex mb-6 overflow-hidden flex-wrap">
        <StatCard label="Total Debt" value={fmt(metrics?.totalDebt)} sub={`${metrics?.instrumentCount || 0} instruments`} accent={COLORS.navy} />
        <StatCard label="Wtd. Avg Rate" value={metrics?.weightedAvgRateDisplay || '—'} sub={`${metrics?.floatingRatePctDisplay || '0%'} floating`} />
        <StatCard label="Overall LTV" value={metrics?.overallLtvDisplay || '—'} sub="Below 55% covenant" accent={COLORS.hunter} />
        <StatCard label="Min DSCR" value={metrics?.dscrDisplay || '—'} sub={trajectory.length > 0 ? `Year ${trajectory.reduce((min: any, d: any) => d.dscr < min.dscr ? d : min, trajectory[0]).year}` : ''} accent={COLORS.gold} />
        <StatCard
          label="Next Maturity"
          value={metrics?.nextMaturity?.date ? new Date(metrics.nextMaturity.date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : '—'}
          sub={metrics?.nextMaturity ? `${metrics.nextMaturity.name.substring(0, 20)} — ${fmt(metrics.nextMaturity.balance)}` : ''}
          accent={COLORS.burgundy}
        />
      </Card>

      {/* Panel Tabs */}
      <div className="flex gap-1 mb-5 bg-stone-200 rounded-sm p-0.5 w-fit">
        {panels.map((p) => (
          <button
            key={p.id}
            onClick={() => setPanel(p.id)}
            className={`px-4 py-1.5 rounded-sm border-none cursor-pointer text-[11px] font-sans font-semibold transition-all duration-150 ${
              panel === p.id
                ? 'text-ivory-100'
                : 'text-stone-500 bg-transparent hover:text-stone-700'
            }`}
            style={panel === p.id ? { background: COLORS.navy, color: COLORS.ivory } : {}}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Active Panel */}
      {panel === 'overview' && (
        <LeverageOverviewPanel trajectory={trajectory} debtSchedule={debtSchedule} metrics={metrics} />
      )}
      {panel === 'covenants' && (
        <>
          {covModal.open && (
            <CovenantFormModal
              item={covModal.item}
              liabilities={debtSchedule}
              onSave={handleSaveCovenant}
              onClose={() => setCovModal({ open: false, item: null })}
            />
          )}
          <CovenantMonitorPanel
            covenants={covenants}
            trajectory={trajectory}
            liabilities={debtSchedule}
            onAdd={handleAddCovenant}
            onEdit={handleEditCovenant}
            onDelete={handleDeleteCovenant}
          />
        </>
      )}
      {panel === 'maturity' && (
        <MaturityRatePanel maturityProfile={maturityProfile} rateExposure={rateExposure} trajectory={trajectory} metrics={metrics} />
      )}
      {panel === 'scenarios' && (
        <ContributionScenariosPanel entities={entities} members={members} />
      )}
    </div>
  );
}
